import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  SingularDayCategory,
  SingularDayDemandBehavior,
  type SingularGroupName,
} from '@ebalance/proto/bffuser/v1/singular_day_model_pb'
import type { TFunction } from 'i18next'

import { AREA_CODE_OPTIONS } from '~/models/area'

/**
 * 特異日カテゴリ / 需要扱いの表示用ヘルパー (純関数)。
 *
 * backend が確定させた enum 値を表示ラベル (i18n key) へ写すだけの表示写像。
 * 業務判断は行わない。
 */

/** カテゴリのセレクト / フィルタ表示順 (design prototype 準拠)。 */
export const SINGULAR_DAY_CATEGORY_OPTIONS: readonly SingularDayCategory[] = [
  SingularDayCategory.YEAR_END_NEW_YEAR,
  SingularDayCategory.OBON,
  SingularDayCategory.GOLDEN_WEEK,
  SingularDayCategory.OTHER,
]

/**
 * 特異グループ番号 (1〜10、spec 定義の隔離キー、design Note
 * singular-day-special-group.md D1/D2)。
 */
export const SINGULAR_DAY_SPECIAL_GROUP_COUNT = 10
export const SINGULAR_DAY_SPECIAL_GROUP_NUMBERS: readonly number[] = Array.from(
  { length: SINGULAR_DAY_SPECIAL_GROUP_COUNT },
  (_, i) => i + 1,
)

/**
 * 需要扱い select の proxy 値 (design `special-day-calendar.html` の select
 * 値 proxy `singular-N` 準拠、竹モック `_editBehaviorKey` 同型)。 単一
 * フラット select が demand_behavior + singular_group の 2 フィールドを
 * まとめて表現するための表示専用の合成値。
 */
export type DemandBehaviorProxyValue = string

const NON_SINGULAR_PROXY_BY_BEHAVIOR: ReadonlyMap<
  SingularDayDemandBehavior,
  DemandBehaviorProxyValue
> = new Map([
  [SingularDayDemandBehavior.WEEKDAY, 'weekday'],
  [SingularDayDemandBehavior.SATURDAY, 'saturday'],
  [SingularDayDemandBehavior.HOLIDAY, 'holiday'],
])

/**
 * demandBehavior + singularGroup (form の 2 フィールド) → select の表示値。
 * SINGULAR は `singular-{singularGroup}`、 それ以外は固定 proxy 値。
 */
export function encodeDemandBehaviorProxy(
  demandBehavior: SingularDayDemandBehavior,
  singularGroup: number,
): DemandBehaviorProxyValue {
  const fixed = NON_SINGULAR_PROXY_BY_BEHAVIOR.get(demandBehavior)
  if (fixed !== undefined) {
    return fixed
  }
  return `singular-${singularGroup}`
}

/**
 * select の表示値 → demandBehavior + singularGroup (form の 2 フィールド)。
 * 平日/土曜/日祝相当を選択した時は singularGroup を既定 1 に戻す (design Note
 * singular-day-special-group.md UI「Interactions」: 「singular_group は既定1
 * のまま送信」)。
 */
export function decodeDemandBehaviorProxy(proxy: DemandBehaviorProxyValue): {
  demandBehavior: SingularDayDemandBehavior
  singularGroup: number
} {
  for (const [behavior, fixed] of NON_SINGULAR_PROXY_BY_BEHAVIOR) {
    if (proxy === fixed) {
      return { demandBehavior: behavior, singularGroup: 1 }
    }
  }
  const n = Number(proxy.slice('singular-'.length))
  const singularGroup = SINGULAR_DAY_SPECIAL_GROUP_NUMBERS.includes(n) ? n : 1
  return { demandBehavior: SingularDayDemandBehavior.SINGULAR, singularGroup }
}

/** カテゴリ enum → i18n label key。 */
export function singularDayCategoryLabelKey(c: SingularDayCategory): string {
  switch (c) {
    case SingularDayCategory.YEAR_END_NEW_YEAR:
      return 'page:adminSingularDay.category.yearEndNewYear'
    case SingularDayCategory.OBON:
      return 'page:adminSingularDay.category.obon'
    case SingularDayCategory.GOLDEN_WEEK:
      return 'page:adminSingularDay.category.goldenWeek'
    case SingularDayCategory.OTHER:
    case SingularDayCategory.UNSPECIFIED:
    default:
      return 'page:adminSingularDay.category.other'
  }
}

/**
 * 特異グループ名の最大文字数 (spec §2 バリデーション表)。 client validation
 * (`SingularGroupNamesModal` / インライン命名欄) と backend proto validate
 * の両方がこの値を上限とする。
 */
export const SINGULAR_GROUP_NAME_MAX_LEN = 30

/** 一括編集の 1 欄の検証結果 (AC-MSD-018 / 019 + 既存の長さ検証)。 */
export type SingularGroupNameErrorKind =
  | 'tooLong'
  | 'duplicate'
  | 'sameAsBehavior'

/** 前後の空白 (半角・全角) を除く (AC-MSD-018 / 019 / 020)。 */
export function trimSingularGroupName(name: string): string {
  return name.replace(/^[\s\u3000]+|[\s\u3000]+$/g, '')
}

/** 名前の比較キー: 前後の空白を除き、 大文字小文字を無視する。 */
function singularGroupNameKey(name: string): string {
  return trimSingularGroupName(name).toLowerCase()
}

const DEFAULT_GROUP_NAME_JA = (n: number): string => `特異グループ${n}`
const DEFAULT_GROUP_NAME_EN = (n: number): string => `Singular group ${n}`

/**
 * 需要扱いの値 (日本語・英語、 AC-MSD-019)。 特異日 CSV の需要扱い列が受け付ける
 * 値と一致させる (表示名から別のものを指さないため)。 表示用ではなく比較用の
 * 定数なので locale には依存させない。
 */
const DEMAND_BEHAVIOR_VALUE_KEYS: ReadonlySet<string> = new Set(
  [
    '平日相当',
    '土曜相当',
    '日祝相当',
    'Weekday equivalent',
    'Saturday equivalent',
    'Sunday and holiday equivalent',
    ...SINGULAR_DAY_SPECIAL_GROUP_NUMBERS.flatMap((n) => [
      DEFAULT_GROUP_NAME_JA(n),
      DEFAULT_GROUP_NAME_EN(n),
    ]),
  ].map(singularGroupNameKey),
)

/**
 * 保存対象の名前を確定する (AC-MSD-020): 前後の空白を除き、 自分の番号の既定名
 * (日本語・英語、 大文字小文字無視) は「名前なし」= 空文字にする。
 */
export function effectiveSingularGroupName(group: number, raw: string): string {
  const key = singularGroupNameKey(raw)
  if (
    key === singularGroupNameKey(DEFAULT_GROUP_NAME_JA(group)) ||
    key === singularGroupNameKey(DEFAULT_GROUP_NAME_EN(group))
  ) {
    return ''
  }
  return trimSingularGroupName(raw)
}

/**
 * 特異グループ名 10 欄の一括検証 (AC-MSD-018 / 019、 30 文字制限)。
 * `rawValues` は全 10 欄の現在値 (未変更欄も含める: 重複は未変更欄との間でも起きる)。
 * 欄ごとに 1 つのエラーを返す。 優先順は 長さ > 需要扱いと同名 > 重複。
 * 空欄 (自分の既定名を含む) は比較しない。
 * 同じ規則は backend (`singulargroupname.NormalizeName` / `ValidateDistinctNames`) も持つ。
 * frontend は欄ごとのエラー表示のための先回りの検証。
 */
export function validateSingularGroupNames(
  rawValues: Readonly<Record<number, string>>,
): Record<number, SingularGroupNameErrorKind> {
  const errors: Record<number, SingularGroupNameErrorKind> = {}
  const groupsByKey = new Map<string, number[]>()
  for (const n of SINGULAR_DAY_SPECIAL_GROUP_NUMBERS) {
    const name = effectiveSingularGroupName(n, rawValues[n] ?? '')
    if (name === '') {
      continue
    }
    if (Array.from(name).length > SINGULAR_GROUP_NAME_MAX_LEN) {
      errors[n] = 'tooLong'
      continue
    }
    const key = singularGroupNameKey(name)
    if (DEMAND_BEHAVIOR_VALUE_KEYS.has(key)) {
      errors[n] = 'sameAsBehavior'
      continue
    }
    groupsByKey.set(key, [...(groupsByKey.get(key) ?? []), n])
  }
  for (const groups of groupsByKey.values()) {
    if (groups.length >= 2) {
      for (const n of groups) {
        errors[n] = 'duplicate'
      }
    }
  }
  return errors
}

/**
 * 特異グループ番号 → 保存済み表示名 の map (`ListSingularGroupNamesResponse.
 * singularGroupNames`, 命名済み行のみ) から構築する。 未命名グループは
 * map に存在しない → `resolveSingularGroupName` が既定名にフォールバック
 * する (design Note singular-group-names.md D3)。
 */
export function buildSingularGroupNameMap(
  rows: readonly SingularGroupName[],
): ReadonlyMap<number, string> {
  return new Map(rows.map((row) => [row.singularGroup, row.name]))
}

/**
 * 特異グループ番号の表示名を解決する (AC-MSD-011 ※注記): 命名済みなら
 * 保存された表示名、 未命名なら既定名「特異グループN」。 既定名の client
 * 生成規則自体は維持し (`page:adminSingularDay.demandBehavior.
 * singularGroupName`)、 保存された name を優先するフォールバックへ差し替える
 * (design Note singular-group-names.md「差し替え点」)。
 */
export function resolveSingularGroupName(
  group: number,
  namesByGroup: ReadonlyMap<number, string>,
  t: TFunction,
): string {
  return (
    namesByGroup.get(group) ??
    t('page:adminSingularDay.demandBehavior.singularGroupName', { n: group })
  )
}

/** 需要扱い enum → i18n label key。 */
export function singularDayDemandBehaviorLabelKey(
  b: SingularDayDemandBehavior,
): string {
  switch (b) {
    case SingularDayDemandBehavior.WEEKDAY:
      return 'page:adminSingularDay.demandBehavior.weekday'
    case SingularDayDemandBehavior.SATURDAY:
      return 'page:adminSingularDay.demandBehavior.saturday'
    case SingularDayDemandBehavior.HOLIDAY:
      return 'page:adminSingularDay.demandBehavior.holiday'
    case SingularDayDemandBehavior.SINGULAR:
    case SingularDayDemandBehavior.UNSPECIFIED:
    default:
      return 'page:adminSingularDay.demandBehavior.singular'
  }
}

/** エリア MultiSelect / フィルタの表示順 (北→南、 UNSPECIFIED 除く 10 個)。 */
export const SINGULAR_DAY_AREA_OPTIONS: readonly AreaCode[] = AREA_CODE_OPTIONS

/**
 * 送信用のエリア正規化 (design §5.5)。
 *
 * - 空 (未選択) → 空 (全エリア共通)
 * - 10 個全選択 → 空 (全エリア共通に正規化)
 * - 1〜9 個 → 昇順に整列した copy
 *
 * backend でも同じ正規化を行うが、 request 段で揃えておくことで
 * cache patch / 表示が backend 応答と一致する。
 */
export function normalizeAreaCodesForSubmit(areaCodes: AreaCode[]): AreaCode[] {
  if (areaCodes.length === 0 || areaCodes.length === AREA_CODE_OPTIONS.length) {
    return []
  }
  return [...areaCodes].sort((a, b) => a - b)
}

/**
 * エリアフィルタ判定 (AC-02 / design `matchesAreaFilter`)。
 *
 * - filter 未選択 (`undefined`) → 全件表示 (true)
 * - 特定エリア選択 → 「当該エリアを含む行」 または 「全エリア共通行
 *   (areaCodes 空)」 を表示
 */
export function matchesSingularDayAreaFilter(
  rowAreaCodes: AreaCode[],
  filter: AreaCode | undefined,
): boolean {
  if (filter === undefined) {
    return true
  }
  return rowAreaCodes.length === 0 || rowAreaCodes.includes(filter)
}
